import React, { useEffect, useRef, useState } from 'react';
import { useArc } from '../context/ArcContext';
import { STRATEGY_CATEGORIES } from '../mock/mock';
import { Copy, Wand2, ChevronRight, ArrowLeft, Check, Loader2, Send, Sparkles } from 'lucide-react';
import { assertBackendUrl } from '../lib/backendUrl';

const API = assertBackendUrl();

const VOICE_STYLES = [
  { key: 'arc', label: 'Arc voice' },
  { key: 'professional', label: 'Professional' },
  { key: 'polite', label: 'Polite' },
  { key: 'casual', label: 'Casual' },
];

// ─── Quick-pick config: per category → per question → per engine ──────────────
// Falls back to `default` when no engine-specific picks exist.
const QUICK_PICKS = {
  cold_outreach: [
    { // Q0: who are you reaching out to?
      default: ['C-Suite at $10M to $100M businesses', 'Founders of VC-backed startups', 'High-net-worth private investors', 'Agency and consultancy owners'],
      TITAN:    ['C-Suite at $50M+ revenue companies', 'Private equity and portfolio leaders', 'Serial acquirers and roll-up founders'],
      SOVEREIGN:['Independent business owners', 'Location-independent founders', 'Portfolio entrepreneurs earning $500K+/yr'],
      ORACLE:   ['Chief Strategy Officers and research directors', 'Pattern-oriented founders and fund managers', 'Analysts and intelligence-led builders'],
      ARTISAN:  ['Creative directors & brand leaders', 'Founders building identity-led businesses', 'Editors, publishers, and cultural leaders'],
    },
    { // Q1: what is the core thing you are offering?
      default: ['A done-for-you system that removes their biggest bottleneck', 'A strategic framework with a proven outcome track record', 'A partnership structure with built-in revenue upside'],
      TITAN:   ['A leverage system that multiplies their output 10x', 'An acquisition framework that compresses 12 months to 90 days'],
      ORACLE:  ['A pattern-recognition model that predicts their next market shift', 'A data model that maps their competitive blind spots'],
      ARTISAN: ['A brand identity system that commands premium pricing', 'A creative direction that separates them from the generic market'],
    },
    { // Q2: specific result you have delivered
      default: ['Generated $X in pipeline in 90 days for a similar client', 'Compressed a 6-month outcome to 6 weeks using this exact system', 'Took a client from $X to $Y MRR in one quarter'],
      TITAN:   ['Closed a $2.4M deal in 3 months for a client at $8M ARR', 'Scaled a client from 3 to 11 enterprise accounts in a single quarter'],
      ALCHEMIST:['Automated a manual process saving 40 hours/week for a 50-person team', 'Built an internal tool that reduced customer churn by 34%'],
    },
    { // Q3: specific trigger for this outreach
      default: ['They just posted about a growth challenge they are facing', 'They recently changed roles or took on a new mandate', 'They are clearly expanding into our space based on recent hires'],
      TITAN:   ['They just raised a round and are under pressure to deploy capital fast', 'Their top competitor just made a major market move'],
      ORACLE:  ['A macro trend I have been tracking just hit their specific sector', 'They published a thesis that aligns with a pattern I have been mapping'],
    },
  ],
  negotiation: [
    { default: ['Closing a retainer with a client who has stalled for 3 weeks', 'Counter-offering equity terms with an investor', 'Negotiating a vendor rate reduction after 12 months of loyalty'] },
    { default: ['Demonstrable ROI from the relationship so far', 'Alternative options I have already vetted', 'My genuine willingness to walk away from this deal'] },
    { default: ['I will not go below $X/month or below Y% equity', 'The deal must close within 2 weeks or I move on', 'No equity without board or governance rights attached'] },
    { default: ['Critical: long-term strategic partner, relationship must survive', 'Moderate: preserve it if possible, but outcome matters more', 'Low: purely transactional, I optimise for the number only'] },
  ],
  positioning: [
    { default: ['Founders at $1M to $10M stuck doing too much low-leverage work', 'Executives launching their own vehicle in the next 12 months', 'Creators monetising under $20K/month who want to scale to $100K+'] },
    { default: ['I have done this myself and carry the outcome receipts', 'I combine two rarely combined skillsets the market undervalues', 'I have access or relationships my target market cannot easily replicate'] },
    { default: ['I help clients go from $X to $Y in Z months', 'My clients consistently land a specific measurable result within 90 days', 'I have eliminated a specific painful bottleneck for a specific type of person'] },
    { default: ['I do not work with people who want passive results', 'I will not provide generic advice. Every engagement is precision-specific', 'I do not take on more than X clients at a time. Ever'] },
  ],
  strategic_planning: [
    { default: ['$250K in revenue by end of Q4', 'Land 3 enterprise clients at $5K/month', 'Build a 1,000-person qualified waiting list in 90 days'] },
    { default: ['At $8K MRR, 2 team members, 5 active clients', '$0 revenue with a working prototype and 200 beta sign-ups', '12 one-off clients, no recurring revenue, fully manual ops still'] },
    { default: ['Solo founder, 35 hours execution time, $50K runway', 'Team of 3, $200K in the bank, 6 months to next milestone', 'Side project while employed full-time, 10 to 15 hours/week available'] },
    { default: ['Following up consistently with warm leads I have not yet converted', 'Outsourcing the build instead of building myself', 'Having a hard conversation about pricing or contract terms'] },
  ],
  financial_moves: [
    { default: ['$4,200/month: rent, food, transport, insurance, subscriptions', '$7,800/month including mortgage, staff, software and lifestyle', '$2,100/month lean digital nomad baseline'] },
    { default: ['$6,500/month freelance consulting + $1,200/month digital products', '$12,000/month salary, zero secondary income yet', '$3,000/month primary + $800/month small online course'] },
    { default: ['A productised consulting offer at $2,500 one-time', 'A monthly retainer structure replacing hourly billing', 'A digital product that generates revenue without my direct time'] },
    { default: ['Cover full foundation with asset income within 24 months', 'Hit $20K/month total with no single client above 30%', 'Build a business I can sell for $1M+ in 3 years'] },
  ],
};

function getQuickPicks(categoryId, qIdx, engineKey) {
  const cat = QUICK_PICKS[categoryId];
  if (!cat || !cat[qIdx]) return [];
  const q = cat[qIdx];
  return (engineKey && q[engineKey]) ? q[engineKey] : (q.default || []);
}

// ─── Chat bubble ──────────────────────────────────────────────────────────────

function ChatBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-[fadeSlideUp_0.22s_ease]`}>
      <div
        className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 text-[13.5px] leading-[1.7] ${isUser ? 'rounded-2xl rounded-br-sm' : 'rounded-2xl rounded-bl-sm'}`}
        style={isUser ? {
          background: 'color-mix(in srgb, var(--el) 16%, transparent)',
          border: '1px solid color-mix(in srgb, var(--el) 42%, transparent)',
          color: '#fff',
        } : {
          background: 'rgba(255,255,255,0.045)',
          border: '1px solid rgba(255,255,255,0.07)',
          color: 'rgba(255,255,255,0.88)',
        }}
      >
        {msg.label && (
          <div className="font-data text-[10px] tracking-[0.2em] uppercase mb-1.5" style={{ color: 'var(--el)', opacity: 0.8 }}>
            {msg.label}
          </div>
        )}
        {msg.text}
        {msg.loading && (
          <span className="inline-flex gap-1 ml-2 align-middle">
            <span className="h-1.5 w-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="h-1.5 w-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="h-1.5 w-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Conversational intake ────────────────────────────────────────────────────

function ConversationalIntake({ category, output, engine, arc, loading, err, onGenerate, onBack }) {
  const qs = category.questions;
  const [messages, setMessages] = useState([
    { role: 'ai', label: `${output.name}`, text: `I'll ask you ${qs.length} questions. Your precision drives the quality of the output.` },
    { role: 'ai', label: `Question 1 of ${qs.length}`, text: qs[0] },
  ]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(Array(qs.length).fill(''));
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const picks = getQuickPicks(category.id, step, engine?.key);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!submitted) inputRef.current?.focus();
  }, [messages, submitted]);

  // Show API error as a bubble
  useEffect(() => {
    if (err && submitted) {
      setMessages(prev => [...prev, { role: 'ai', text: '⚠ ' + err }]);
      setSubmitted(false);
    }
  }, [err, submitted]);

  const submit = (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || submitted) return;

    const nextAnswers = [...answers];
    nextAnswers[step] = trimmed;
    setAnswers(nextAnswers);
    setInput('');

    const newMsgs = [...messages, { role: 'user', text: trimmed }];

    if (step + 1 < qs.length) {
      newMsgs.push({ role: 'ai', label: `Question ${step + 2} of ${qs.length}`, text: qs[step + 1] });
      setMessages(newMsgs);
      setStep(step + 1);
    } else {
      newMsgs.push({ role: 'ai', text: `Composing your ${output.name} in ${arc?.key || 'your Arc'}'s voice…`, loading: true });
      setMessages(newMsgs);
      setSubmitted(true);
      onGenerate(nextAnswers);
    }
  };

  return (
    <div className="panel-strong rounded-2xl overflow-hidden flex flex-col" style={{ minHeight: 480 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/6">
        <button className="btn btn-ghost px-2 py-1.5 text-[12px]" onClick={onBack} disabled={submitted}>
          <ArrowLeft size={13} /> Back
        </button>
        <div className="text-center">
          <div className="font-data text-[10px] tracking-[0.2em] uppercase text-white/40">{category.name}</div>
          <div className="font-head text-[14px] text-white/85">{output.name}</div>
        </div>
        <div className="font-data text-[11px] text-white/35 min-w-[40px] text-right">
          {submitted ? 'Done' : `${step + 1}/${qs.length}`}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ maxHeight: '45vh' }}>
        {messages.map((m, i) => <ChatBubble key={i} msg={m} />)}
        {loading && !messages.find(m => m.loading) && (
          <ChatBubble msg={{ role: 'ai', text: 'Composing…', loading: true }} />
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick picks + input (hidden once submitted) */}
      {!submitted && (
        <div className="border-t border-white/6 px-4 pt-3 pb-4 space-y-3">
          {picks.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {picks.map((p, i) => (
                <button
                  key={i}
                  onClick={() => submit(p)}
                  className="px-3 py-1.5 rounded-full text-[11px] leading-snug border border-white/10 bg-white/3 hover:bg-white/8 hover:border-white/20 text-white/65 hover:text-white/90 transition-colors text-left"
                >
                  {p}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              rows={2}
              className="input flex-1 resize-none text-[13.5px]"
              placeholder="Your answer… (Enter to submit, Shift+Enter for new line)"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(input); } }}
            />
            <button
              onClick={() => submit(input)}
              disabled={!input.trim()}
              className="btn btn-primary px-3 py-3 self-stretch flex items-center justify-center"
              style={{ minWidth: 44 }}
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function StrategyStudio() {
  const { state, update, engine, arc } = useArc();
  const [category, setCategory] = useState(null);
  const [output, setOutput] = useState(null);
  const [generated, setGenerated] = useState(null);
  const [refine, setRefine] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refining, setRefining] = useState(false);
  const [err, setErr] = useState('');
  const [voiceStyle, setVoiceStyle] = useState('arc');

  const reset = () => { setCategory(null); setOutput(null); setGenerated(null); setRefine(''); setErr(''); setLoading(false); };

  const saveStrategyOutput = (text, answers, refinement = null) => {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      categoryId: category?.id || null,
      categoryName: category?.name || null,
      outputId: output?.id || null,
      outputName: output?.name || null,
      voiceStyle,
      engineKey: engine?.key || null,
      arcKey: arc?.key || null,
      answers: answers || null,
      refinement,
      text,
      createdAt: new Date().toISOString(),
    };
    update({ strategyOutputs: [entry, ...((state.strategyOutputs || []).slice(0, 49))] });
  };

  const onGenerate = async (answers) => {
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ai/strategy`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          category_id: category.id, category_name: category.name,
          output_id: output.id, output_name: output.name,
          questions: category.questions, answers,
          engine_key: engine?.key || 'TITAN', arc_key: arc?.key || 'your Arc', voice_style: voiceStyle
        })
      });
      if (!r.ok) throw new Error('AI request failed');
      const data = await r.json();
      setGenerated(data.text);
      saveStrategyOutput(data.text, answers);
    } catch {
      setErr('The Studio could not reach its source. Try again in a moment.');
    } finally { setLoading(false); }
  };

  const onRefine = async () => {
    if (!refine.trim() || !generated) return;
    setRefining(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ai/strategy/refine`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ original: generated, instruction: refine, engine_key: engine?.key || 'TITAN', arc_key: arc?.key || 'your Arc', voice_style: voiceStyle })
      });
      if (!r.ok) throw new Error('Refine failed');
      const data = await r.json();
      setGenerated(data.text);
      saveStrategyOutput(data.text, null, refine.trim());
      setRefine('');
    } catch { setErr('Could not refine. Try again.'); } finally { setRefining(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(generated); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch {}
  };

  return (
    <div className="fade-up space-y-5">
      {/* Header */}
      <div>
        <h1 className="font-head text-[26px] md:text-[34px] text-white/95">Strategy Studio</h1>
        <p className="text-white/55 text-[13px] mt-1 leading-[1.65]">
          No templates. Answer {category ? category.questions.length : 'a few'} questions and receive a practical deliverable written in {arc?.key || 'your Arc'}'s voice.
        </p>
      </div>

      <div className="panel p-3 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-data text-[10px] tracking-[0.18em] uppercase text-white/40">Output voice</div>
          <div className="text-[12px] text-white/50 mt-0.5">Choose how the deliverable should sound.</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {VOICE_STYLES.map(style => (
            <button
              key={style.key}
              type="button"
              onClick={() => setVoiceStyle(style.key)}
              className="px-3 py-2 rounded-lg border text-[11px] font-data uppercase tracking-[0.12em] transition-colors"
              style={{
                borderColor: voiceStyle === style.key ? 'color-mix(in srgb, var(--el) 55%, transparent)' : 'rgba(255,255,255,0.08)',
                background: voiceStyle === style.key ? 'color-mix(in srgb, var(--el) 10%, transparent)' : 'rgba(255,255,255,0.02)',
                color: voiceStyle === style.key ? 'var(--el)' : 'rgba(255,255,255,0.55)',
              }}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 1: category picker */}
      {!category && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STRATEGY_CATEGORIES.map(c => (
            <button key={c.id} onClick={() => setCategory(c)} className="panel p-5 text-left transition-all hover:border-white/15 active:scale-[0.98]">
              <div className="rail-left font-head text-[15px] text-white">{c.name}</div>
              <p className="text-white/55 text-[13px] mt-2 leading-[1.65]">{c.blurb}</p>
              <div className="mt-4 flex items-center gap-1.5 text-[11px] font-data uppercase tracking-widest" style={{ color: 'var(--el)' }}>
                {c.outputs.length} outputs <ChevronRight size={11} />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Step 2: output picker */}
      {category && !output && (
        <div className="panel-strong p-5 rounded-2xl">
          <button className="btn btn-ghost mb-5 px-2 py-1.5 text-[12px]" onClick={reset}>
            <ArrowLeft size={13} /> All categories
          </button>
          <div className="flex items-start gap-3 mb-5">
            <Sparkles size={16} style={{ color: 'var(--el)', marginTop: 3 }} />
            <div>
              <div className="font-head text-[18px] text-white">{category.name}</div>
              <p className="text-white/55 text-[13px] mt-1">Choose the specific deliverable.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {category.outputs.map(o => (
              <button key={o.id} onClick={() => setOutput(o)} className="panel p-4 text-left hover:border-white/15 active:scale-[0.98] transition-all">
                <div className="text-white/90 text-[14.5px]">{o.name}</div>
                <div className="mt-2 text-[11px] font-data uppercase tracking-widest flex items-center gap-1" style={{ color: 'var(--el)' }}>
                  Begin intake <ChevronRight size={11} className="inline" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: conversational intake */}
      {category && output && !generated && (
        <ConversationalIntake
          category={category}
          output={output}
          engine={engine}
          arc={arc}
          loading={loading}
          err={err}
          onGenerate={onGenerate}
          onBack={() => { setOutput(null); setErr(''); }}
        />
      )}

      {/* Step 4: result */}
      {generated && (
        <div className="panel-strong p-5 rounded-2xl" data-testid="strategy-result">
          {/* Result header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] font-data uppercase tracking-[0.2em] text-white/40">{category?.name} · {output?.name}</div>
              <div className="rail-left font-head text-[15px] text-white mt-0.5">Deliverable · {arc?.key}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button className="btn px-3 py-2 text-[12px]" onClick={copy} data-testid="strategy-copy-btn">
                {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
              </button>
              <button className="btn btn-ghost px-3 py-2 text-[12px]" onClick={reset} data-testid="strategy-new-btn">New output</button>
            </div>
          </div>

          {/* Generated output */}
          <pre className="p-4 rounded-xl whitespace-pre-wrap font-body text-white/88 text-[13.5px] leading-[1.7] overflow-x-auto" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {generated}
          </pre>

          {/* Refine row */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              className="input flex-1 text-[13.5px]"
              placeholder="Refine… e.g. make it shorter, more direct, rewrite for enterprise"
              value={refine}
              onChange={e => setRefine(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !refining && onRefine()}
              disabled={refining}
              data-testid="strategy-refine-input"
            />
            <button
              className="btn btn-primary px-4 py-2.5 text-[13px] whitespace-nowrap"
              onClick={onRefine}
              disabled={refining || !refine.trim()}
              data-testid="strategy-refine-btn"
            >
              {refining ? <><Loader2 size={13} className="animate-spin" /> Refining…</> : <><Wand2 size={13} /> Refine</>}
            </button>
          </div>
          {err && <div className="mt-3 text-[12px] text-red-400/85">{err}</div>}
        </div>
      )}
    </div>
  );
}
